import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { updateBalance } from '@redux/actions/playerDetailsActions'
import { getSessionUser } from '@redux/selectors'
import {
  TYPE_BALANCE_UPDATE,
  PING_MESSAGE,
  PING_INTERVAL_MS,
  STABLE_CONNECTION_MS,
} from './WebSocket.constants'
import {
  buildSocketUrl,
  getReconnectDelay,
  parseMessage,
  isForPlayer,
  toBalanceUpdate,
} from './WebSocket.functions'

const WebSocketContext = createContext(null)

export { WebSocketContext }

// Opens the realtime stream for the player on screen while the component is
// mounted. Only one player page is open at a time.
export const useRealtimePlayer = (playerId) => {
  const watchPlayer = useContext(WebSocketContext)?.watchPlayer

  useEffect(() => {
    if (!watchPlayer || !playerId) {
      return undefined
    }

    return watchPlayer(playerId)
  }, [watchPlayer, playerId])
}

function Socket({ children }) {
  // --- Local state -----------------------------------------------------------
  const [playerId, setPlayerId] = useState(null)
  // --- END: Local state ------------------------------------------------------

  // --- Refs ------------------------------------------------------------------
  const listeners = useRef(new Set())
  // --- END: Refs -------------------------------------------------------------

  // --- Redux -----------------------------------------------------------------
  const dispatch = useDispatch()
  const userId = useSelector(getSessionUser)?.id
  // --- END: Redux ------------------------------------------------------------

  // --- Side effects ----------------------------------------------------------
  useEffect(() => {
    if (!userId || !playerId) {
      return undefined
    }

    const url = buildSocketUrl(window._env_?.REACT_APP_WS_URL, playerId)
    if (!url) {
      console.warn(
        'REACT_APP_WS_URL is not set, so the player page will not update live',
      )
      return undefined
    }

    let current
    let pingTimer
    let stableTimer
    let reconnectTimer
    let attempt = 0
    let disposed = false

    const handleMessage = (e) => {
      const message = parseMessage(e.data)
      if (!message || !isForPlayer(message, playerId)) {
        return
      }

      if (message.type === TYPE_BALANCE_UPDATE) {
        dispatch(updateBalance(toBalanceUpdate(message)))
      }

      // Status events are never patched into the store: the payload is the
      // status row, not the shape the sidebar and avatar read (previous
      // status, end date, next status), so patching it would drop the
      // self-exclusion and time-out state. Live refresh asks for a refresh.

      // One failing listener must not keep the message from the others.
      listeners.current.forEach((listener) => {
        try {
          listener(message)
        } catch (err) {
          console.error('Realtime listener failed', message.type, err)
        }
      })
    }

    const connect = () => {
      current = new WebSocket(url)

      current.addEventListener('open', () => {
        stableTimer = setTimeout(() => {
          attempt = 0
        }, STABLE_CONNECTION_MS)
        pingTimer = setInterval(() => {
          if (current.readyState === WebSocket.OPEN) {
            current.send(PING_MESSAGE)
          }
        }, PING_INTERVAL_MS)
      })

      current.addEventListener('message', handleMessage)

      current.addEventListener('close', () => {
        clearInterval(pingTimer)
        clearTimeout(stableTimer)
        if (disposed) {
          return
        }
        reconnectTimer = setTimeout(connect, getReconnectDelay(attempt))
        attempt += 1
      })
    }

    connect()

    return () => {
      disposed = true
      clearInterval(pingTimer)
      clearTimeout(stableTimer)
      clearTimeout(reconnectTimer)
      current.close()
    }
  }, [userId, playerId, dispatch])
  // --- END: Side effects -----------------------------------------------------

  // --- Data and handlers -----------------------------------------------------
  // Registers a listener for every parsed message; returns the unsubscribe.
  const subscribe = useCallback((listener) => {
    listeners.current.add(listener)
    return () => listeners.current.delete(listener)
  }, [])

  // Scopes the stream to one player; returns the release.
  const watchPlayer = useCallback((id) => {
    setPlayerId(id)
    return () => setPlayerId((current) => (current === id ? null : current))
  }, [])

  const ws = useMemo(
    () => ({ subscribe, watchPlayer }),
    [subscribe, watchPlayer],
  )
  // --- END: Data and handlers ------------------------------------------------

  return (
    <WebSocketContext.Provider value={ws}>{children}</WebSocketContext.Provider>
  )
}

Socket.propTypes = {
  children: PropTypes.element,
}

Socket.defaultProps = {
  children: null,
}

export default Socket
